import { useState, useEffect, useCallback } from "react";
import {
  ClipboardList,
  CheckCircle2,
  Circle,
  Clock,
  Ban,
  ChevronDown,
  ChevronRight,
  Users,
  AlertTriangle,
  ArrowRightLeft,
  Plus,
  Loader2,
  ListTodo,
  CalendarDays,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/constants";
import { TASK_STATUS_CONFIG, TASK_URGENCY, TASK_CATEGORY_COLORS } from "@/lib/taskStatusConfig";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

const STATUS_CONFIG = TASK_STATUS_CONFIG;
const CATEGORY_COLORS = TASK_CATEGORY_COLORS;

function TaskStatusButton({ task, onUpdate }) {
  const [updating, setUpdating] = useState(false);
  const nextStatus = {
    "Not Started": "In Progress",
    "In Progress": "Done",
    "Blocked": "In Progress",
    "Done": "Not Started",
  };

  const handleClick = async () => {
    setUpdating(true);
    try {
      const res = await fetch(`${API}/scrum/tasks/${task.task_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: nextStatus[task.status] }),
      });
      if (res.ok) {
        onUpdate();
        toast.success(`Task marked as ${nextStatus[task.status]}`);
      }
    } catch {
      toast.error("Failed to update task");
    } finally {
      setUpdating(false);
    }
  };

  const cfg = STATUS_CONFIG[task.status] || STATUS_CONFIG["Not Started"];
  const Icon = cfg.icon;

  return (
    <button
      onClick={handleClick}
      disabled={updating}
      className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors hover:opacity-80 ${cfg.bg}`}
      title={`Click to mark as ${nextStatus[task.status]}`}
      data-testid={`task-status-btn-${task.task_id}`}
    >
      {updating ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : (
        <Icon className={`w-3 h-3 ${cfg.color}`} />
      )}
      <span className={cfg.color}>{task.status}</span>
    </button>
  );
}

function TaskRow({ task, onUpdate }) {
  const today = new Date().toISOString().split("T")[0];
  const isOverdue = task.due_date < today && task.status !== "Done";
  const isDueToday = task.due_date === today;

  return (
    <div
      className={`flex items-start gap-2 px-2.5 py-2 rounded-lg border transition-colors ${
        isOverdue ? TASK_URGENCY.overdue.bg : isDueToday ? TASK_URGENCY.dueToday.bg : TASK_URGENCY.normal.bg
      }`}
      data-testid={`scrum-task-${task.task_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-xs font-medium ${task.status === "Done" ? "line-through text-slate-400" : "text-slate-800"}`}>
            {task.title}
          </span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[task.category] || "bg-slate-100 text-slate-600"}`}>
            {task.category}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
          {task.owner_name && <span className="font-medium text-slate-500">{task.owner_name}</span>}
          {task.client_name && <span>{task.client_name}</span>}
          <span className={isOverdue ? TASK_URGENCY.overdue.text : isDueToday ? TASK_URGENCY.dueToday.text : ""}>
            {isOverdue ? `Overdue (${task.due_date})` : isDueToday ? "Due today" : `Due ${task.due_date}`}
          </span>
        </div>
      </div>
      <TaskStatusButton task={task} onUpdate={onUpdate} />
    </div>
  );
}

function QuickTaskForm({ onCreated, onClose }) {
  const [title, setTitle] = useState("");
  const [owner, setOwner] = useState("");
  const [dueDate, setDueDate] = useState(new Date().toISOString().split("T")[0]);
  const [clientName, setClientName] = useState("");
  const [category, setCategory] = useState("Delivery");
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/scrum/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: title.trim(),
          owner_name: owner.trim(),
          due_date: dueDate,
          client_name: clientName.trim(),
          category,
        }),
      });
      if (res.ok) {
        toast.success("Task created");
        onCreated();
        setTitle("");
        setOwner("");
        setClientName("");
      }
    } catch {
      toast.error("Failed to create task");
    } finally {
      setCreating(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-2.5 bg-blue-50/50 border border-blue-200 rounded-lg space-y-2" data-testid="quick-task-form">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-700">Quick Task</span>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="What needs to be done?"
        className="w-full text-xs px-2.5 py-1.5 rounded border border-slate-200 focus:outline-none focus:border-blue-400"
        data-testid="quick-task-title"
        autoFocus
      />
      <div className="grid grid-cols-2 gap-1.5">
        <input
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          placeholder="Owner"
          className="text-[11px] px-2 py-1 rounded border border-slate-200 focus:outline-none focus:border-blue-400"
          data-testid="quick-task-owner"
        />
        <input
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          placeholder="Client"
          className="text-[11px] px-2 py-1 rounded border border-slate-200 focus:outline-none focus:border-blue-400"
          data-testid="quick-task-client"
        />
      </div>
      <div className="flex gap-1.5">
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="text-[11px] px-2 py-1 rounded border border-slate-200 focus:outline-none focus:border-blue-400 flex-1"
          data-testid="quick-task-due-date"
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="text-[11px] px-2 py-1 rounded border border-slate-200 focus:outline-none focus:border-blue-400"
          data-testid="quick-task-category"
        >
          <option>Delivery</option>
          <option>Proposal</option>
          <option>Follow-up</option>
          <option>Ops</option>
          <option>Finance</option>
        </select>
      </div>
      <Button type="submit" size="sm" disabled={!title.trim() || creating} className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1.5" data-testid="quick-task-submit">
        {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
        Create Task
      </Button>
    </form>
  );
}

export function ScrumBriefPanel({ onSendMessage }) {
  const [brief, setBrief] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [taskSummary, setTaskSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuickTask, setShowQuickTask] = useState(false);
  const [expandedSection, setExpandedSection] = useState("tasks");
  const [taskFilter, setTaskFilter] = useState("all");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [briefRes, tasksRes, summaryRes] = await Promise.all([
        fetch(`${API}/scrum/brief/today`, { credentials: "include" }),
        fetch(`${API}/scrum/tasks?include_done=false`, { credentials: "include" }),
        fetch(`${API}/scrum/tasks/summary`, { credentials: "include" }),
      ]);
      if (briefRes.ok) setBrief(await briefRes.json());
      if (tasksRes.ok) {
        const d = await tasksRes.json();
        setTasks(d.tasks || []);
      }
      if (summaryRes.ok) setTaskSummary(await summaryRes.json());
    } catch (e) {
      console.error("Error fetching scrum data:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredTasks = tasks.filter((t) => {
    if (taskFilter === "all") return true;
    if (taskFilter === "overdue") return t.due_date < new Date().toISOString().split("T")[0];
    if (taskFilter === "today") return t.due_date === new Date().toISOString().split("T")[0];
    return t.owner_name?.toLowerCase().includes(taskFilter.toLowerCase());
  });

  const owners = [...new Set(tasks.map((t) => t.owner_name).filter(Boolean))];
  const today = new Date().toISOString().split("T")[0];
  const overdueCount = tasks.filter((t) => t.due_date < today).length;
  const dueTodayCount = tasks.filter((t) => t.due_date === today).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
      </div>
    );
  }

  const movements = brief?.pipeline_movement?.items || [];

  return (
    <div className="space-y-2" data-testid="scrum-brief-panel">
      {/* Stats bar */}
      <div className="flex gap-2 flex-wrap" data-testid="scrum-stats-bar">
        <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border cursor-pointer transition-colors ${
          taskFilter === "overdue" ? TASK_URGENCY.overdue.filterActive : overdueCount > 0 ? TASK_URGENCY.overdue.filterInactive : "bg-slate-50 border-slate-200 text-slate-500"
        }`} onClick={() => setTaskFilter(taskFilter === "overdue" ? "all" : "overdue")} data-testid="filter-overdue">
          <AlertTriangle className="w-3 h-3" />
          {overdueCount} overdue
        </div>
        <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border cursor-pointer transition-colors ${
          taskFilter === "today" ? TASK_URGENCY.dueToday.filterActive : TASK_URGENCY.dueToday.filterInactive
        }`} onClick={() => setTaskFilter(taskFilter === "today" ? "all" : "today")} data-testid="filter-today">
          <CalendarDays className="w-3 h-3" />
          {dueTodayCount} due today
        </div>
        <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border cursor-pointer transition-colors ${
          taskFilter === "all" ? TASK_URGENCY.normal.filterActive : TASK_URGENCY.normal.filterInactive
        }`} onClick={() => setTaskFilter("all")} data-testid="filter-all">
          <ListTodo className="w-3 h-3" />
          {tasks.length} open
        </div>
        {taskSummary?.blocked > 0 && (
          <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${TASK_STATUS_CONFIG["Blocked"].badgeBg}`}>
            <Ban className="w-3 h-3" />
            {taskSummary.blocked} blocked
          </div>
        )}
      </div>

      {/* Owner filter pills */}
      {owners.length > 0 && (
        <div className="flex gap-1 flex-wrap">
          {owners.map((o) => (
            <button
              key={o}
              onClick={() => setTaskFilter(taskFilter === o ? "all" : o)}
              className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${
                taskFilter === o ? "bg-blue-100 border-blue-300 text-blue-700" : "bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100"
              }`}
              data-testid={`filter-owner-${o}`}
            >
              {o} ({tasks.filter((t) => t.owner_name === o).length})
            </button>
          ))}
        </div>
      )}

      {/* Quick task form */}
      {showQuickTask ? (
        <QuickTaskForm onCreated={() => { fetchAll(); setShowQuickTask(false); }} onClose={() => setShowQuickTask(false)} />
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs gap-1.5 border-dashed border-slate-300 text-slate-500 hover:text-blue-600 hover:border-blue-300"
          onClick={() => setShowQuickTask(true)}
          data-testid="add-task-btn"
        >
          <Plus className="w-3 h-3" />
          Add Task
        </Button>
      )}

      {/* Task list */}
      <div data-testid="scrum-task-list">
        <button
          onClick={() => setExpandedSection(expandedSection === "tasks" ? "" : "tasks")}
          className="flex items-center gap-1.5 w-full text-left text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5 hover:text-blue-600 transition-colors"
          data-testid="toggle-tasks-section"
        >
          {expandedSection === "tasks" ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <ClipboardList className="w-3 h-3" />
          Tasks ({filteredTasks.length})
        </button>
        {expandedSection === "tasks" && (
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {filteredTasks.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-3">No tasks matching filter</p>
            ) : (
              filteredTasks.map((t) => <TaskRow key={t.task_id} task={t} onUpdate={fetchAll} />)
            )}
          </div>
        )}
      </div>

      {/* Pipeline Movement */}
      {movements.length > 0 && (
        <div data-testid="pipeline-movement-section">
          <button
            onClick={() => setExpandedSection(expandedSection === "pipeline" ? "" : "pipeline")}
            className="flex items-center gap-1.5 w-full text-left text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5 hover:text-blue-600 transition-colors"
            data-testid="toggle-pipeline-section"
          >
            {expandedSection === "pipeline" ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            <ArrowRightLeft className="w-3 h-3" />
            Pipeline Movement ({movements.length})
          </button>
          {expandedSection === "pipeline" && (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {movements.slice(0, 10).map((m, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 px-2.5 py-1.5 rounded-lg border border-slate-100 bg-white hover:border-slate-200 transition-colors cursor-pointer"
                  onClick={() => onSendMessage?.(`Tell me more about ${m.client}`)}
                  data-testid={`movement-${i}`}
                >
                  <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                    m.type === "win" ? "bg-emerald-500" : m.type === "loss" ? "bg-red-500" : m.type === "new_lead" ? "bg-blue-500" : "bg-amber-400"
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-slate-800">{m.client}</div>
                    <div className="text-[10px] text-slate-400">
                      {m.what_changed}
                      {m.owner ? ` — ${m.owner}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Scrum actions shortcut */}
      <div className="pt-1.5 border-t border-slate-100">
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs gap-1.5 text-slate-600 hover:text-blue-600 hover:border-blue-300"
          onClick={() => onSendMessage?.("Show me today's scrum brief")}
          data-testid="scrum-brief-chat-btn"
        >
          <ClipboardList className="w-3 h-3" />
          Full Scrum Brief in Chat
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs gap-1.5 text-slate-600 hover:text-blue-600 hover:border-blue-300 mt-1"
          onClick={() => onSendMessage?.("Capture scrum actions")}
          data-testid="capture-actions-btn"
        >
          <Users className="w-3 h-3" />
          Capture Scrum Actions
        </Button>
      </div>
    </div>
  );
}
